import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getKpiList } from '../../modules/targetModule/targetKpi';
import * as targetApi from '../../lib/api/target';

/**
 * KPI 정의 관리 컨테이너 (관리자용)
 */
const TargetKpiSettingContainer = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  
  const [selectedTargetTyp, setSelectedTargetTyp] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    kpiUuid: '',
    targetTyp: '',
    kpiCd: '',
    kpiNm: '',
    kpiDesc: '',
    kpiUnit: '',
    dataTyp: 'NUMBER',
    decimalPoint: 0,
    sortOrder: 0,
    useYn: 'Y',
  });

  const { kpiList } = useSelector(({ targetModule }) => ({
    kpiList: targetModule.targetKpi.kpiList,
  }));

  // 목표 유형 옵션
  const targetTypeOptions = [
    { value: 'SALES', label: '영업 목표' },
    { value: 'PRODUCTION', label: '생산 목표' },
    { value: 'TECH', label: '기술 목표' },
    { value: 'QC', label: '품질 목표' },
  ];

  // 데이터 타입 옵션
  const dataTypeOptions = [
    { value: 'NUMBER', label: '정수' },
    { value: 'DECIMAL', label: '소수' },
    { value: 'PERCENT', label: '백분율' },
  ];

  // KPI 목록 조회
  const fetchKpiList = useCallback(() => {
    const params = selectedTargetTyp ? { targetTyp: selectedTargetTyp } : {};
    dispatch(getKpiList(params));
  }, [dispatch, selectedTargetTyp]);

  useEffect(() => {
    fetchKpiList();
  }, [fetchKpiList]);

  // 다이얼로그 열기 (추가)
  const handleAdd = () => {
    setFormData({
      kpiUuid: '',
      targetTyp: selectedTargetTyp || 'SALES',
      kpiCd: '',
      kpiNm: '',
      kpiDesc: '',
      kpiUnit: '',
      dataTyp: 'NUMBER',
      decimalPoint: 0,
      sortOrder: 0,
      useYn: 'Y',
    });
    setEditMode(false);
    setDialogOpen(true);
  };

  // 다이얼로그 열기 (수정)
  const handleEdit = (kpi) => {
    setFormData({
      kpiUuid: kpi.kpiUuid,
      targetTyp: kpi.targetTyp,
      kpiCd: kpi.kpiCd,
      kpiNm: kpi.kpiNm,
      kpiDesc: kpi.kpiDesc || '',
      kpiUnit: kpi.kpiUnit || '',
      dataTyp: kpi.dataTyp || 'NUMBER',
      decimalPoint: kpi.decimalPoint || 0,
      sortOrder: kpi.sortOrder || 0,
      useYn: kpi.useYn || 'Y',
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  // 저장
  const handleSave = async () => {
    if (!formData.targetTyp || !formData.kpiCd || !formData.kpiNm) {
      enqueueSnackbar('필수 항목을 입력하세요', { variant: 'warning' });
      return;
    }

    try {
      if (editMode) {
        await targetApi.updateKpiDef(formData.kpiUuid, formData);
        enqueueSnackbar('KPI가 수정되었습니다', { variant: 'success' });
      } else {
        await targetApi.createKpiDef(formData);
        enqueueSnackbar('KPI가 등록되었습니다', { variant: 'success' });
      }
      setDialogOpen(false);
      fetchKpiList();
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      enqueueSnackbar(`저장 실패: ${message}`, { variant: 'error' });
    }
  };

  // 삭제
  const handleDelete = async (kpiUuid) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await targetApi.deleteKpiDef(kpiUuid);
      enqueueSnackbar('KPI가 삭제되었습니다', { variant: 'success' });
      fetchKpiList();
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      enqueueSnackbar(`삭제 실패: ${message}`, { variant: 'error' });
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">KPI 정의 관리</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          KPI 추가
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* 필터 */}
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>목표 유형</InputLabel>
          <Select
            value={selectedTargetTyp}
            label="목표 유형"
            onChange={(e) => setSelectedTargetTyp(e.target.value)}
          >
            <MenuItem value="">전체</MenuItem>
            {targetTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* KPI 목록 테이블 */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>목표 유형</TableCell>
              <TableCell>KPI 코드</TableCell>
              <TableCell>KPI 명칭</TableCell>
              <TableCell>단위</TableCell>
              <TableCell>데이터 타입</TableCell>
              <TableCell align="center">정렬순서</TableCell>
              <TableCell align="center">사용여부</TableCell>
              <TableCell align="center">관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {kpiList.map((kpi) => (
              <TableRow key={kpi.kpiUuid} hover>
                <TableCell>
                  <Chip 
                    label={targetTypeOptions.find(o => o.value === kpi.targetTyp)?.label || kpi.targetTyp} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{kpi.kpiCd}</TableCell>
                <TableCell>{kpi.kpiNm}</TableCell>
                <TableCell>{kpi.kpiUnit}</TableCell>
                <TableCell>{dataTypeOptions.find(o => o.value === kpi.dataTyp)?.label || kpi.dataTyp}</TableCell>
                <TableCell align="center">{kpi.sortOrder}</TableCell>
                <TableCell align="center">
                  <Chip 
                    label={kpi.useYn === 'Y' ? '사용' : '미사용'} 
                    size="small" 
                    color={kpi.useYn === 'Y' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleEdit(kpi)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(kpi.kpiUuid)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {kpiList.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  등록된 KPI가 없습니다
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* KPI 등록/수정 다이얼로그 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'KPI 수정' : 'KPI 등록'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small" required disabled={editMode}>
              <InputLabel>목표 유형</InputLabel>
              <Select
                value={formData.targetTyp}
                label="목표 유형"
                onChange={(e) => setFormData({ ...formData, targetTyp: e.target.value })}
              >
                {targetTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="KPI 코드"
              size="small"
              required
              disabled={editMode}
              value={formData.kpiCd}
              onChange={(e) => setFormData({ ...formData, kpiCd: e.target.value.toUpperCase() })}
              helperText="영문 대문자, 언더스코어 사용 (예: PROJECT_SALES)"
            />

            <TextField
              label="KPI 명칭"
              size="small"
              required
              value={formData.kpiNm}
              onChange={(e) => setFormData({ ...formData, kpiNm: e.target.value })}
            />

            <TextField
              label="설명"
              size="small"
              multiline
              rows={2}
              value={formData.kpiDesc}
              onChange={(e) => setFormData({ ...formData, kpiDesc: e.target.value })}
            />

            <TextField
              label="단위"
              size="small"
              value={formData.kpiUnit}
              onChange={(e) => setFormData({ ...formData, kpiUnit: e.target.value })}
              helperText="예: KRW, EA, %, 건, 시간"
            />

            <FormControl fullWidth size="small">
              <InputLabel>데이터 타입</InputLabel>
              <Select
                value={formData.dataTyp}
                label="데이터 타입"
                onChange={(e) => setFormData({ ...formData, dataTyp: e.target.value })}
              >
                {dataTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="소수점 자릿수"
              size="small"
              type="number"
              value={formData.decimalPoint}
              onChange={(e) => setFormData({ ...formData, decimalPoint: parseInt(e.target.value) || 0 })}
            />

            <TextField
              label="정렬 순서"
              size="small"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.useYn === 'Y'}
                  onChange={(e) => setFormData({ ...formData, useYn: e.target.checked ? 'Y' : 'N' })}
                />
              }
              label="사용 여부"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleSave}>저장</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TargetKpiSettingContainer;
